/**
 * Team Builder screen — compact card layout.
 * Each Pokemon card has: sprite preview, nickname, species, ability/nature/level/gender inline,
 * 4 moves inline, EV/IV sliders, computed stats.
 */

import type { ScreenId } from "./home";
import type { Team, PokemonSet, StatKey } from "../../engine/types";
import { el, clearChildren } from "../utils/dom";
import { button, dropdown, autocomplete, numberInput, toast, modal, statSlider } from "../components";
import {
  getAllSpecies, getAllMoves, getAllAbilities, getAllItems, getAllNatures,
  getSpeciesAbilities, getSpeciesTypes, getBaseStats, calcStat,
} from "../../data/gen5";
import { validateTeam } from "../../engine/validator";
import { randomizeTeam } from "../../engine/randomizer";
import { saveTeam, getAllTeams, deleteTeam, createEmptyTeam, setLastTeamId, getLastTeamId } from "../../data/teams";
import { parseShowdownText, exportShowdownText } from "../../data/showdown";
import { downloadJson, downloadBlob, pickFile, readFileAsText, exportZip, collectCookieData } from "../../data/backup";
import { pokemonFrontUrl } from "../../data/images";
import { resolveSpriteUrl } from "../../data/sprites";
import { MAX_TEAM_SIZE, MAX_EVS, MAX_EV_PER_STAT, MAX_IV, MAX_LEVEL, DEFAULT_LEVEL } from "../../config/constants";

const STAT_KEYS: StatKey[] = ["hp", "atk", "def", "spa", "spd", "spe"];
const STAT_LABELS: Record<StatKey, string> = { hp: "HP", atk: "Atk", def: "Def", spa: "SpA", spd: "SpD", spe: "Spe" };

export function renderTeambuilder(container: HTMLElement, navigate: (screen: ScreenId) => void): void {
  clearChildren(container);

  const header = el("header", { className: "app-header" });
  header.appendChild(el("h1", { text: "Team Builder" }));
  header.appendChild(button("Back to Home", () => navigate("home"), { variant: "secondary" }));
  container.appendChild(header);

  const main = el("main", { className: "app-main teambuilder-main" });

  // Team selector
  const teamSection = el("section", { className: "team-section" });
  const teamBar = el("div", { className: "team-bar" });
  const teamSelect = el("select", { className: "dropdown" }) as HTMLSelectElement;
  teamBar.appendChild(teamSelect);
  teamBar.appendChild(button("New", () => { saveTeam(createEmptyTeam()).then(() => loadAndRender()); }, { variant: "secondary" }));
  teamBar.appendChild(button("Del", () => { if (teamSelect.value) deleteTeam(teamSelect.value).then(() => { toast("Team deleted", "success"); loadAndRender(); }); }, { variant: "danger" }));
  teamSection.appendChild(teamBar);

  const teamNameInput = el("input", { className: "text-input", attrs: { type: "text", placeholder: "Team name" } }) as HTMLInputElement;
  teamSection.appendChild(teamNameInput);
  main.appendChild(teamSection);

  // Members
  const membersList = el("div", { className: "members-list" });
  main.appendChild(membersList);

  // Actions
  const actionsBar = el("div", { className: "actions-bar" });
  actionsBar.appendChild(button("Randomize", async () => {
    toast("Generating...", "info");
    try { const team = await randomizeTeam(); currentTeam = team; await saveTeam(team); setLastTeamId(team.id); toast("Random team generated!", "success"); await loadAndRender(); }
    catch { toast("Failed to generate team", "error"); }
  }));
  actionsBar.appendChild(button("Validate", () => {
    const result = validateTeam(currentTeam);
    if (result.valid) toast("Team is valid!", "success");
    else modal("Validation Errors", result.errors.map((e) => el("p", { text: `- ${e}` })));
  }));
  actionsBar.appendChild(button("Save", async () => {
    currentTeam.name = teamNameInput.value || "Untitled"; await saveTeam(currentTeam); setLastTeamId(currentTeam.id); toast("Saved!", "success");
  }));

  const ioBar = el("div", { className: "actions-bar" });
  ioBar.appendChild(button("Import JSON", async () => {
    const file = await pickFile("application/json"); if (!file) return;
    try { const text = await readFileAsText(file); const data = JSON.parse(text); const team = Array.isArray(data) ? { ...createEmptyTeam(), members: data } : data; currentTeam = team; await saveTeam(team); toast("Imported!", "success"); await loadAndRender(); }
    catch { toast("Failed to import JSON", "error"); }
  }, { variant: "secondary" }));
  ioBar.appendChild(button("Export JSON", () => downloadJson(currentTeam, `${currentTeam.name || "team"}.json`), { variant: "secondary" }));
  ioBar.appendChild(button("Showdown In", async () => {
    const file = await pickFile("text/plain"); if (!file) return;
    try { const text = await readFileAsText(file); const sets = parseShowdownText(text); currentTeam.members = sets; await saveTeam(currentTeam); toast(`Imported ${sets.length}!`, "success"); renderMembers(); }
    catch { toast("Failed", "error"); }
  }, { variant: "secondary" }));
  ioBar.appendChild(button("Showdown Out", () => { const blob = new Blob([exportShowdownText(currentTeam.members)], { type: "text/plain" }); downloadBlob(blob, `${currentTeam.name || "team"}.txt`); }, { variant: "secondary" }));
  ioBar.appendChild(button("ZIP Backup", async () => {
    toast("Creating backup...", "info");
    try { const { themeId, trainer, lastTeamId } = collectCookieData(); const blob = await exportZip(await getAllTeams(), trainer, themeId, [], lastTeamId); downloadBlob(blob, "pbs-backup.zip"); toast("Backup downloaded!", "success"); }
    catch { toast("Backup failed", "error"); }
  }, { variant: "secondary" }));

  main.appendChild(actionsBar);
  main.appendChild(ioBar);
  container.appendChild(main);

  let currentTeam: Team = createEmptyTeam();

  async function loadAndRender(): Promise<void> {
    const teams = await getAllTeams();
    clearChildren(teamSelect);
    if (teams.length === 0) { const t = createEmptyTeam(); await saveTeam(t); teams.push(t); }
    for (const t of teams) teamSelect.appendChild(el("option", { text: t.name || "Untitled", attrs: { value: t.id } }));
    const lastId = getLastTeamId() ?? teams[0].id;
    currentTeam = teams.find((t) => t.id === lastId) ?? teams[0];
    teamSelect.value = currentTeam.id;
    teamNameInput.value = currentTeam.name;
    teamSelect.onchange = async () => {
      const sel = teams.find((t) => t.id === teamSelect.value);
      if (sel) { currentTeam = sel; teamNameInput.value = currentTeam.name; setLastTeamId(currentTeam.id); renderMembers(); }
    };
    renderMembers();
  }

  function renderMembers(): void {
    clearChildren(membersList);
    for (let i = 0; i < currentTeam.members.length; i++) {
      membersList.appendChild(renderMemberCard(currentTeam.members[i], i));
    }
    if (currentTeam.members.length < MAX_TEAM_SIZE) {
      membersList.appendChild(button("+ Add Pokemon", () => { currentTeam.members.push(createEmptySet()); renderMembers(); }, { variant: "secondary", className: "add-pokemon-btn" }));
    }
  }

  function renderMemberCard(set: PokemonSet, index: number): HTMLElement {
    const card = el("div", { className: "pokemon-card" });

    // Top row: slot number + sprite preview + remove
    const topRow = el("div", { className: "card-top-row" });
    const spriteImg = el("img", { className: "card-sprite-preview", attrs: { alt: set.species || "No sprite" } }) as HTMLImageElement;
    spriteImg.style.imageRendering = "pixelated";
    if (set.species) {
      const url = pokemonFrontUrl(set.species, set.shiny);
      void resolveSpriteUrl(url).then((resolved) => { if (resolved) spriteImg.src = resolved; });
    }
    topRow.appendChild(spriteImg);

    const topInfo = el("div", { className: "card-top-info" });
    topInfo.appendChild(el("span", { text: `${index + 1}. ${set.name || set.species || "Empty"}`, className: "card-slot-label" }));
    const types = getSpeciesTypes(set.species);
    const typeContainer = el("div", { className: "type-badges" });
    for (const t of types) typeContainer.appendChild(el("span", { text: t, className: `type-badge type-${t.toLowerCase()}` }));
    topInfo.appendChild(typeContainer);
    topRow.appendChild(topInfo);

    topRow.appendChild(button("X", () => { currentTeam.members.splice(index, 1); renderMembers(); }, { variant: "danger", className: "remove-pokemon-btn" }));
    card.appendChild(topRow);

    // Nickname + Species row
    const nickRow = el("div", { className: "card-row-2col" });
    nickRow.appendChild(autocomplete("Nickname", [], set.name ?? "", (val) => { set.name = val; }, { placeholder: "Nickname" }).container);
    nickRow.appendChild(autocomplete("Species", getAllSpecies(), set.species, (val) => {
      set.species = val;
      if (!set.ability) { const ab = getSpeciesAbilities(val); if (ab.length > 0) set.ability = ab[0]; }
      renderMembers();
    }).container);
    card.appendChild(nickRow);

    // Ability + Nature row
    const anRow = el("div", { className: "card-row-2col" });
    const abilities = set.species ? getSpeciesAbilities(set.species) : getAllAbilities();
    anRow.appendChild(dropdown("Ability", abilities.map((a) => ({ value: a, label: a })), set.ability, (v) => { set.ability = v; }).container);
    anRow.appendChild(dropdown("Nature", getAllNatures().map((n) => ({ value: n, label: n })), set.nature ?? "", (v) => { set.nature = v; }).container);
    card.appendChild(anRow);

    // Item + Level + Gender row
    const ilgRow = el("div", { className: "card-row-3col" });
    ilgRow.appendChild(autocomplete("Item", getAllItems(), set.item ?? "", (v) => { set.item = v; }, { placeholder: "None" }).container);
    ilgRow.appendChild(numberInput("Level", set.level ?? DEFAULT_LEVEL, (v) => { set.level = v; }, { min: 1, max: MAX_LEVEL }).container);
    ilgRow.appendChild(dropdown("Gender", [{ value: "", label: "Any" }, { value: "M", label: "M" }, { value: "F", label: "F" }], set.gender ?? "", (v) => { set.gender = v as "" | "M" | "F"; }).container);
    card.appendChild(ilgRow);

    // Shiny toggle (compact)
    const shinyLabel = el("label", { className: "checkbox-label" });
    const shinyCb = el("input", { attrs: { type: "checkbox" } }) as HTMLInputElement;
    shinyCb.checked = set.shiny ?? false;
    shinyCb.addEventListener("change", () => { set.shiny = shinyCb.checked; renderMembers(); });
    shinyLabel.appendChild(shinyCb);
    shinyLabel.appendChild(el("span", { text: " Shiny" }));
    card.appendChild(shinyLabel);

    // Moves — 2x2 grid
    const movesGrid = el("div", { className: "moves-grid" });
    for (let m = 0; m < 4; m++) {
      movesGrid.appendChild(autocomplete("", getAllMoves(), set.moves[m] ?? "", (v) => { set.moves[m] = v; }, { placeholder: `Move ${m + 1}` }).container);
    }
    card.appendChild(movesGrid);

    // EV sliders + IV sliders side by side
    const statsRow = el("div", { className: "card-row-2col" });

    // EVs
    const evSection = el("div", { className: "stat-section" });
    evSection.appendChild(el("label", { text: "EVs", className: "stat-section-label" }));
    let evTotal = 0;
    for (const sk of STAT_KEYS) {
      const val = set.evs?.[sk] ?? 0;
      evTotal += val;
      evSection.appendChild(statSlider(STAT_LABELS[sk], val, (v) => { if (!set.evs) set.evs = {} as Partial<Record<StatKey, number>>; set.evs[sk] = v; }, { min: 0, max: MAX_EV_PER_STAT }).container);
    }
    evSection.appendChild(el("span", { text: `Total: ${evTotal}/${MAX_EVS}`, className: "stat-total" }));
    statsRow.appendChild(evSection);

    // IVs
    const ivSection = el("div", { className: "stat-section" });
    ivSection.appendChild(el("label", { text: "IVs", className: "stat-section-label" }));
    for (const sk of STAT_KEYS) {
      ivSection.appendChild(statSlider(STAT_LABELS[sk], set.ivs?.[sk] ?? 31, (v) => { if (!set.ivs) set.ivs = {} as Partial<Record<StatKey, number>>; set.ivs[sk] = v; }, { min: 0, max: MAX_IV }).container);
    }
    statsRow.appendChild(ivSection);
    card.appendChild(statsRow);

    // Computed stats preview
    if (set.species) {
      const baseStats = getBaseStats(set.species);
      if (baseStats && Object.keys(baseStats).length > 0) {
        const preview = el("div", { className: "stat-preview" });
        const statGrid = el("div", { className: "computed-stats-grid" });
        for (const sk of STAT_KEYS) {
          const computed = calcStat(sk, baseStats[sk] ?? 0, set.ivs?.[sk] ?? 31, set.evs?.[sk] ?? 0, set.level ?? DEFAULT_LEVEL, set.nature);
          statGrid.appendChild(el("div", { className: "computed-stat-cell", text: `${STAT_LABELS[sk]} ${computed}` }));
        }
        preview.appendChild(statGrid);
        card.appendChild(preview);
      }
    }
    return card;
  }

  void loadAndRender();
}

function createEmptySet(): PokemonSet {
  return {
    species: "", ability: "", moves: [],
    level: DEFAULT_LEVEL,
    evs: { hp: 0, atk: 0, def: 0, spa: 0, spd: 0, spe: 0 },
    ivs: { hp: 31, atk: 31, def: 31, spa: 31, spd: 31, spe: 31 },
    gender: "", shiny: false, happiness: 255,
  };
}